import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import statement is only for npm/yarn installations, NOT for CDN users
import Betterbugs from '@betterbugs/web-sdk';
import * as Sentry from '@sentry/react';

const isDevelopment =
  import.meta.env.MODE === 'development' || import.meta.env.DEV;

// Initialize Sentry as early as possible in your application's lifecycle
Sentry.init({
  dsn: 'https://f376805e8482d6911f22b3a18833eba1@sentry.cfbtel.com/3',
  environment: import.meta.env.MODE || 'development',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: isDevelopment ? 1.0 : 0.1, // Capture 100% in dev, 10% in production
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/dev\.backend\.bpo24\.osring\.com\/api/,
    /^https:\/\/.*\.bpo24\.osring\.com\/api/,
    /^https:\/\/.*\.backend\.bpo24\.osring\.com\/api/,
  ],
  // Session Replay
  replaysSessionSampleRate: isDevelopment ? 1.0 : 0.1, // 100% in dev, 10% in production
  replaysOnErrorSampleRate: 1.0, // Always capture replays when errors occur
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Avoid infinite refetch loops on network/CORS failures
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Initialize BetterBugs widget - export for use in other components if needed
export const bb = new Betterbugs({
  apiKey: 'e9f885c818f3988b9ea02d7f1de49dba', // Replace with your actual API key
});

createRoot(document.getElementById('root')!).render(
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          Something went wrong
        </h1>
        <p className="max-w-lg text-sm text-gray-600">
          An error occurred and has been reported to our team.
        </p>
        {isDevelopment && error ? (
          <pre className="mt-4 max-w-2xl overflow-auto rounded bg-red-50 p-4 text-left text-xs text-red-800">
            {error instanceof Error ? error.toString() : String(error)}
          </pre>
        ) : null}
        <div className="flex gap-3">
          <button
            onClick={resetError}
            className="rounded-md bg-[#78C7D1] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#63b2bd]"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-white"
          >
            Reload page
          </button>
        </div>
      </div>
    )}
  >
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);
